import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, KeyRound, CheckCircle2, Building2, Bell, Mail } from "lucide-react";
import { sr, actAs } from "../../lib/sr";

const CAP_HELP = {
  "platform.admin": "Administer the whole platform",
  "clients.view": "View client accounts",
  "clients.manage": "Create and manage client accounts",
  "campaigns.view": "View campaigns and their projects",
  "campaigns.create": "Create new campaigns",
  "campaigns.edit": "Edit campaigns and update workflow tasks",
  "campaigns.approve": "Approve and sign off campaigns",
  "approvals.review": "Review and comment on approvals",
  "digital.operate": "Run digital / DSP delivery",
  "digital.qa": "QA digital campaign setup",
  "radio.operate": "Run radio delivery and scheduling",
  "radio.qa": "QA radio delivery",
  "creative.produce": "Produce creative, copy and scripts",
  "creative.qa": "QA creative and brand safety",
  "measurement.view": "View measurement and reporting",
  "users.manage": "Manage team members and their roles",
  "billing.manage": "Manage billing and invoices",
  "franchise.manage": "Manage the franchise hierarchy",
  "act_as": "Act on behalf of a client",
};

export default function MyAccess() {
  const [data, setData] = useState(null);
  const [np, setNp] = useState(null);
  const [savingNp, setSavingNp] = useState(false);
  const acting = actAs.get();

  useEffect(() => {
    const p = acting?.tenant_id
      ? sr.rbacCapabilities("client", acting.tenant_id)
      : sr.rbacCapabilities();
    p.then(setData).catch(() => setData({ error: true }));
    sr.notifyPrefs().then(setNp).catch(() => {});
  }, []);

  const toggleNp = (kind, channel) => setNp((s) => ({
    ...s, prefs: { ...s.prefs, [kind]: { ...s.prefs[kind], [channel]: !s.prefs[kind][channel] } } }));
  const saveNp = async () => {
    setSavingNp(true);
    try { const r = await sr.setNotifyPrefs(np.prefs); setNp((s) => ({ ...s, prefs: r.prefs })); toast.success("Notification preferences saved"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not save"); }
    finally { setSavingNp(false); }
  };

  if (!data) return <div className="sr-empty"><Loader2 className="sr-spin" /></div>;
  if (data.error) return <div className="sr-card"><p className="sr-muted">Could not load your access.</p></div>;

  const caps = data.capabilities || [];

  return (
    <div data-testid="my-access-page">
      <div style={{ marginBottom: 16 }}>
        <span className="sr-badge" style={{ marginBottom: 8 }}><KeyRound size={12} /> My access</span>
        <h1 style={{ fontSize: 30 }}>What you can do</h1>
        <p className="sr-muted" style={{ marginTop: 6 }}>
          {acting?.tenant_id
            ? <>Your effective permissions for <strong>{acting.name}</strong>.</>
            : <>Your effective permissions across SmartReach.</>}
        </p>
      </div>

      {acting?.tenant_id && (
        <div className="sr-card" style={{ marginBottom: 16, display: "flex", alignItems: "center", gap: 8 }} data-testid="access-scope">
          <Building2 size={16} style={{ color: "var(--blue)" }} />
          <span style={{ fontSize: 13, color: "var(--navy)" }}>Scoped to client: <strong>{acting.name}</strong></span>
        </div>
      )}

      {caps.length === 0 ? (
        <div className="sr-card"><p className="sr-muted" style={{ fontSize: 13 }}>You do not have any permissions in this scope yet.</p></div>
      ) : (
        <div className="sr-card" data-testid="access-list">
          <div style={{ display: "grid", gap: 10 }}>
            {caps.map((c) => (
              <div key={c} style={{ display: "flex", alignItems: "center", gap: 10 }} data-testid={`cap-${c}`}>
                <CheckCircle2 size={16} style={{ color: "#067647", flexShrink: 0 }} />
                <span style={{ fontSize: 14, color: "var(--navy)" }}>{CAP_HELP[c] || c}</span>
                <span className="sr-badge sr-badge-gray" style={{ fontSize: 10, marginLeft: "auto" }}>{c}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {np && (
        <div className="sr-card" style={{ marginTop: 18 }} data-testid="notify-prefs-card">
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
            <Bell size={16} style={{ color: "var(--blue)" }} />
            <h3 style={{ fontSize: 16, color: "var(--navy)" }}>How you get notified</h3>
          </div>
          <p className="sr-muted" style={{ fontSize: 12, marginBottom: 12 }}>Choose which nudges hit your bell versus your inbox.</p>
          <div style={{ display: "grid", gap: 10 }}>
            {np.kinds.map((k) => {
              const p = np.prefs[k.key] || {};
              const chip = (channel, Icon, label) => (
                <button onClick={() => toggleNp(k.key, channel)} data-testid={`np-${k.key}-${channel}`}
                  style={{ display: "inline-flex", alignItems: "center", gap: 5, border: "1px solid var(--border)", cursor: "pointer",
                    borderRadius: 999, padding: "5px 12px", fontSize: 12, fontWeight: 700,
                    background: p[channel] ? "var(--blue)" : "#fff", color: p[channel] ? "#fff" : "var(--muted)" }}>
                  <Icon size={12} /> {label}
                </button>
              );
              return (
                <div key={k.key} style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", borderBottom: "1px solid var(--border)", paddingBottom: 10 }}>
                  <span style={{ flex: "1 1 auto", minWidth: 180, fontSize: 14, color: "var(--navy)" }}>{k.label}</span>
                  {chip("bell", Bell, "Bell")}
                  {chip("email", Mail, "Email")}
                </div>
              );
            })}
          </div>
          <button className="sr-btn sr-btn-primary sr-btn-sm" style={{ marginTop: 12 }} onClick={saveNp} disabled={savingNp} data-testid="notify-prefs-save">
            {savingNp ? <Loader2 size={14} className="sr-spin" /> : "Save preferences"}
          </button>
        </div>
      )}
    </div>
  );
}
